import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import {
  ArrowLeft,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  User,
  Building2,
  Wrench,
  Target,
  AlertTriangle,
  // Settings, // Kept for future use
  // RefreshCw, // Kept for future use
} from 'lucide-react';
import { Checkpoint } from '../../../types/checkpoint';
import { checkpointService } from '../../../services/checkpointService';
import PageBreadcrumb from '../../../shared/PageBreadCrumb';

// Modern Brand Colors
// const BRAND_COLORS = { // Kept for future use
//   primary: "#339274",
//   primaryHover: "#29795f",
//   primaryLight: "#F8FCFA",
//   primarySoft: "#EDF7F2",
//   accent: "#A3CD39",
//   dark: "#007F66",
//   success: "#10B981",
//   successLight: "#F0FDF4",
//   error: "#EF4444",
//   errorLight: "#FEF2F2",
//   warning: "#F59E0B",
//   warningLight: "#FFFBEB",
// };

export default function CheckpointDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  // ========== State Management ==========
  const [checkpoint, setCheckpoint] = useState<Checkpoint | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // ========== Data Fetching ==========
  
  const fetchCheckpoint = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await checkpointService.getCheckpointById(id);
      setCheckpoint(response.data);
    } catch (error: any) {
      console.error('Failed to fetch checkpoint:', error);
      setError(error.response?.data?.message || 'Failed to load checkpoint details');
      toast.error('Failed to load checkpoint details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCheckpoint();
  }, [id]);

  // ========== Event Handlers ==========

  const handleEdit = () => {
    if (checkpoint) {
      navigate(`/masters/checkpoints/${checkpoint._id}/edit`);
    }
  };

  const handleToggleStatus = async () => {
    if (!checkpoint) return;

    try {
      await checkpointService.toggleCheckpointStatus(checkpoint._id);
      setCheckpoint(prev => prev ? {
        ...prev,
        status: prev.status === 'active' ? 'inactive' : 'active'
      } : null);
      toast.success(`Checkpoint ${checkpoint.status === 'active' ? 'deactivated' : 'activated'} successfully`);
    } catch (error) {
      console.error('Failed to toggle checkpoint status:', error);
      toast.error('Failed to update checkpoint status');
    }
  };

  const handleDelete = async () => {
    if (!checkpoint) return;

    try {
      await checkpointService.deleteCheckpoint(checkpoint._id);
      toast.success('Checkpoint deleted successfully');
      navigate('/masters/checkpoints');
    } catch (error) {
      console.error('Failed to delete checkpoint:', error);
      toast.error('Failed to delete checkpoint');
    }
  };

  const handleBack = () => {
    navigate('/masters/checkpoints');
  };

  // ========== Utility Functions ==========

  const renderStatusBadge = (status: string) => {
    const isActive = status === 'active';
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isActive
          ? 'bg-green-100 text-green-800 border border-green-200'
          : 'bg-gray-100 text-gray-800 border border-gray-200'
      }`}>
        <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
          isActive ? 'bg-green-500' : 'bg-gray-400'
        }`} />
        {isActive ? 'active' : 'Inactive'}
      </span>
    );
  };

  const renderFrequencyBadge = (frequency: string) => {
    const frequencyColors = {
      'Daily': 'bg-blue-100 text-blue-800 border-blue-200',
      'Weekly': 'bg-green-100 text-green-800 border-green-200',
      'Monthly': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Quarterly': 'bg-purple-100 text-purple-800 border-purple-200',
      'Half-Yearly': 'bg-orange-100 text-orange-800 border-orange-200',
      'Yearly': 'bg-red-100 text-red-800 border-red-200',
    };

    const colors = frequencyColors[frequency as keyof typeof frequencyColors] || 'bg-gray-100 text-gray-800 border-gray-200';

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${colors}`}>
        {frequency}
      </span>
    );
  };

  // ========== Loading State ==========
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-300 border-t-green-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading checkpoint details...</p>
        </div>
      </div>
    );
  }

  // ========== Error State ==========
  
  if (error || !checkpoint) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageBreadcrumb
          items={[
            { name: "Masters", path: "/masters" },
            { name: "Checkpoints", path: "/masters/checkpoints" },
            { name: "Details", path: "#" },
          ]}
        />
        
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-center">
              <AlertTriangle size={48} className="text-red-500 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Checkpoint Not Found</h2>
              <p className="text-gray-600 mb-4">
                {error || 'The checkpoint you are looking for does not exist or has been removed.'}
              </p>
              <button
                onClick={handleBack}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200"
              >
                <ArrowLeft size={16} />
                Back to Checkpoints
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ========== Render ==========
  
  return (
    <div className="min-h-screen bg-gray-50">
      <PageBreadcrumb
        items={[
          { name: "Masters", path: "/masters" },
          { name: "Checkpoints", path: "/masters/checkpoints" },
          { name: checkpoint.name, path: "#" },
        ]}
      />
      
      <div className="max-w-4xl mx-auto p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleBack}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Target size={20} className="text-green-600" />
                    </div>
                    <div>
                      <h1 className="text-xl font-semibold text-gray-900">{checkpoint.name}</h1>
                      <p className="text-sm text-gray-600">Checkpoint Details</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleToggleStatus}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors duration-200 ${
                      checkpoint.status === 'active'
                        ? 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
                        : 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
                    }`}
                  >
                    {checkpoint.status === 'active' ? (
                      <>
                        <XCircle size={16} />
                        Deactivate
                      </>
                    ) : (
                      <>
                        <CheckCircle size={16} />
                        Activate
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={handleEdit}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors duration-200"
                  >
                    <Edit size={16} />
                    Edit
                  </button>
                  
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors duration-200"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>
              </div>
            </div>

            {/* Basic Information */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Name</h3>
                  <p className="text-sm text-gray-900">{checkpoint.name}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Status</h3>
                  {renderStatusBadge(checkpoint.status)}
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Frequency</h3>
                  {renderFrequencyBadge(checkpoint.frequency || 'Weekly')}
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Project Type</h3>
                  <div className="flex items-center gap-2">
                    <Building2 size={16} className="text-gray-400" />
                    <span className="text-sm text-gray-900">
                      {typeof checkpoint.projectTypeId === 'object' 
                        ? checkpoint.projectTypeId.name 
                        : 'N/A'}
                    </span>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Asset</h3>
                  <div className="flex items-center gap-2">
                    <Wrench size={16} className="text-gray-400" />
                    <span className="text-sm text-gray-900">
                      {typeof checkpoint.assetId === 'object' 
                        ? checkpoint.assetId.name 
                        : 'N/A'}
                    </span>
                  </div>
                </div>
                
                {/* Description removed - using name field instead */}
              </div>
            </div>
          </div>

          {/* Additional Details */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Additional Information</h2>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Created By</h3>
                  <div className="flex items-center gap-2">
                    <User size={16} className="text-gray-400" />
                    <span className="text-sm text-gray-900">
                      {typeof checkpoint.createdBy === 'object' 
                        ? checkpoint.createdBy.name 
                        : 'N/A'}
                    </span>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Created At</h3>
                  <div className="flex items-center gap-2">
                    <Calendar size={16} className="text-gray-400" />
                    <span className="text-sm text-gray-900">
                      {format(new Date(checkpoint.createdAt), 'PPP')}
                    </span>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Last Updated By</h3>
                  <div className="flex items-center gap-2">
                    <User size={16} className="text-gray-400" />
                    <span className="text-sm text-gray-900">
                      {typeof checkpoint.updatedBy === 'object' 
                        ? checkpoint.updatedBy.name 
                        : 'N/A'}
                    </span>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Last Updated</h3>
                  <div className="flex items-center gap-2">
                    <Clock size={16} className="text-gray-400" />
                    <span className="text-sm text-gray-900">
                      {format(new Date(checkpoint.updatedAt), 'PPP')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle size={20} className="text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Checkpoint</h3>
                  <p className="text-sm text-gray-600">This action cannot be undone.</p>
                </div>
              </div>
              
              <p className="text-sm text-gray-700 mb-6">
                Are you sure you want to delete the checkpoint "{checkpoint.name}"? This will permanently remove it from the system.
              </p>
              
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors duration-200"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 